import { useRef, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { type Lift } from '@/hooks';
import { LiftMarker } from './LiftMarker';

interface FloorPlanCanvasProps {
  imageUrl: string;
  lifts: Lift[];
  getPosition: (lift: Lift) => { x: number; y: number } | null;
  onPositionChange: (liftId: string, x: number, y: number) => void;
}

export function FloorPlanCanvas({
  imageUrl,
  lifts,
  getPosition,
  onPositionChange,
}: FloorPlanCanvasProps) {
  const { t } = useTranslation('settings');
  const containerRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState<string | null>(null);

  const handleMouseDown = useCallback((liftId: string) => {
    setDragging(liftId);
  }, []);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!dragging || !containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;

      // Clamp to 0-100
      const clampedX = Math.max(0, Math.min(100, x));
      const clampedY = Math.max(0, Math.min(100, y));

      onPositionChange(dragging, clampedX, clampedY);
    },
    [dragging, onPositionChange]
  );

  const handleMouseUp = useCallback(() => {
    setDragging(null);
  }, []);

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!dragging || !containerRef.current) return;
      e.preventDefault();

      const touch = e.touches[0];
      if (!touch) return;
      const rect = containerRef.current.getBoundingClientRect();
      const x = ((touch.clientX - rect.left) / rect.width) * 100;
      const y = ((touch.clientY - rect.top) / rect.height) * 100;

      const clampedX = Math.max(0, Math.min(100, x));
      const clampedY = Math.max(0, Math.min(100, y));

      onPositionChange(dragging, clampedX, clampedY);
    },
    [dragging, onPositionChange]
  );

  return (
    <Box
      ref={containerRef}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleMouseUp}
      sx={{
        position: 'relative',
        width: '100%',
        cursor: dragging ? 'grabbing' : 'default',
        userSelect: 'none',
      }}
    >
      <Box
        component="img"
        src={imageUrl}
        alt={t('floorPlan.imageAlt')}
        sx={{
          width: '100%',
          display: 'block',
          pointerEvents: 'none',
        }}
      />

      {lifts.map((lift) => {
        const pos = getPosition(lift);
        if (!pos) return null;

        return (
          <LiftMarker
            key={lift.id}
            lift={lift}
            x={pos.x}
            y={pos.y}
            isDragging={dragging === lift.id}
            onMouseDown={() => { handleMouseDown(lift.id); }}
            onTouchStart={() => { handleMouseDown(lift.id); }}
          />
        );
      })}
    </Box>
  );
}
